import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, FileText, Users, ChevronRight, Clock, CheckCircle, XCircle, Briefcase, Search, Award } from 'lucide-react';

const statusConfig = {
  APPROVED:           { label: 'Approved',          color: 'bg-green-100 text-green-700',   icon: CheckCircle },
  PENDING_APPROVAL:   { label: 'Pending Approval',  color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  REJECTED:           { label: 'Rejected',           color: 'bg-red-100 text-red-700',       icon: XCircle },
  PARTIAL_REGISTERED: { label: 'Incomplete Profile', color: 'bg-orange-100 text-orange-700', icon: Clock },
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const status = statusConfig[user?.status] || statusConfig.PENDING_APPROVAL;
  const StatusIcon = status.icon;

  const cards = [
    { icon: User,      bg: 'bg-teal-50',   color: 'text-teal-600',   title: 'My Profile',           desc: 'View and update your details',         path: '/profile' },
    { icon: Briefcase, bg: 'bg-blue-50',   color: 'text-blue-600',   title: 'My Opportunities',     desc: 'Direct hire requests from companies',  path: '/opportunities' },
    { icon: Search,    bg: 'bg-green-50',  color: 'text-green-600',  title: 'Browse Opportunities', desc: 'Apply to open job postings',           path: '/staff/opportunities' },
    { icon: Award,     bg: 'bg-orange-50', color: 'text-orange-600', title: 'My Placements',        desc: 'Current and past work engagements',    path: '/staff/placements' },
    { icon: FileText,  bg: 'bg-indigo-50', color: 'text-indigo-600', title: 'My Documents',         desc: 'View uploaded credentials',            path: '/profile' },
    { icon: Users,     bg: 'bg-purple-50', color: 'text-purple-600', title: 'My Referrals',         desc: `Code: ${user?.referralCode || '—'}`,  path: '/referrals' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <img src="/logo-horizontal.png" alt="ShramSena" className="h-8 w-auto" />
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-gray-600 font-medium">{user?.firstName} {user?.lastName}</span>
            <span className={"flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold " + status.color}>
              <StatusIcon className="h-3.5 w-3.5" />{status.label}
            </span>
            <button onClick={() => { logout(); navigate('/login'); }}
              className="flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-700 transition">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </nav>
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-gradient-to-r from-slate-900 to-teal-900 rounded-2xl p-8 mb-8 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 85% 50%, rgba(20,184,166,0.25) 0%, transparent 60%)' }} />
          <div className="relative">
            <p className="text-teal-400 text-xs font-bold uppercase tracking-[0.15em] mb-2">Staff Portal</p>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">Welcome back, {user?.firstName}!</h1>
            <p className="text-slate-400 text-sm">
              {user?.status === 'APPROVED' ? 'Your profile is verified. Employers can discover you on ShramSena.'
                : user?.status === 'PENDING_APPROVAL' ? "Your profile is under review. We'll notify you once approved."
                : user?.status === 'PARTIAL_REGISTERED' ? 'Complete your profile to get verified and visible to employers.'
                : 'Your registration has been reviewed.'}
            </p>
            {user?.status === 'PARTIAL_REGISTERED' && (
              <button onClick={() => navigate('/complete-registration')}
                className="mt-4 flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition shadow-lg shadow-teal-500/30">
                Complete Profile <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4">Quick Access</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.title} onClick={() => navigate(c.path)}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col gap-4 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all">
                <div className={"w-12 h-12 " + c.bg + " rounded-xl flex items-center justify-center"}>
                  <Icon className={"h-6 w-6 " + c.color} />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-slate-800 mb-1">{c.title}</h3>
                  <p className="text-gray-400 text-sm">{c.desc}</p>
                </div>
                <div className="flex items-center gap-1 text-teal-600 text-sm font-semibold">
                  Open <ChevronRight className="h-4 w-4" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}